import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as DataListAPI from '../../../../api/dataList';
import * as FinanceiroAPI from '../../../../api/financeiro';
import * as ParametroAPI from '../../../../api/parametro';
import * as PessoaAPI from '../../../../api/pessoa';
import * as ProdutoAPI from '../../../../api/produto';

import { formatarNumeroDecimal } from '../../../../utils/masks';
import { checarCnhHandler, obterCnhCategorias } from '../../../../api/cnh';
import { formatCnhFeedback } from '../../../../utils/cnhFeedback';

import { relatorios } from '../../../../api/relatorios';

import { applyLancamentoBoot } from '../boot/applyLancamentoBoot';
import { applyLancamentoPessoa } from '../boot/applyLancamentoPessoa';

/**
 * useLancamentoModule (Caixa)
 * - Estado e handlers do lançamento
 * - Refresh da grid (MRT manual)
 * - Mapa de abastecimento (on/off)
 *
 * Contrato:
 *   return { state, actions }
 */
export function useLancamentoModule({
    // contexto do caixa
    dados,
    setDados,
    parametro,
    empresaId,
    empresaIds,
    pessoaId,

    // ui/util
    btnText,
    setFlashMessage,
    navigate,
    // options externas (por enquanto vêm do CreateOrEdit)
    optionsTipo = [],
    optionsConta = [],
    optionsForma = [],
    // MRT bridge (vem do Form)
    mrt,
    activeTabKey,
}) {
    const isActive = activeTabKey === 'lancamentos';

    // -------------------------
    // options/objetos auxiliares
    // -------------------------
    const [optionsParceiro, setOptionsParceiro] = useState([]);
    const [optionsCentroCusto, setOptionsCentroCusto] = useState([]);

    const [optionsPrazo, setOptionsPrazo] = useState([]);

    const [Parceiro, setParceiro] = useState(null);

    const [Conta, setConta] = useState(null);
    const [pagamentoForma, setForma] = useState(null);
    const [pagamentoPrazo, setPrazo] = useState(null);
    const [centroCusto, setCentroCusto] = useState(null);

    const [optionsMotorista, setOptionsMotorista] = useState([]);
    const [optionsVTR, setOptionsVTR] = useState([]);
    const [divMapaAbastecimento, setDivMapaAbastecimento] = useState(false);
    const [mapaAbastecimento, setMapaAbastecimento] = useState({
        mapa: false,
        ativo: true,
    });

    const [caixaLancamentos, setCaixaLancamentos] = useState([]);

    const pessoaIdPadrao = parametro?.pessoaId;

    const [novoLancamento, setNovoLancamento] = useState(() =>
        createNovoLancamento({ parametro, pessoaId: pessoaIdPadrao, dados })
    );

    // --------------------------------
    // bootstrap quando muda caixa/data
    // --------------------------------
    useEffect(() => {
        setDados((prev) => ({
            ...prev,
            caixalancamentos: caixaLancamentos,
        }));
    }, [caixaLancamentos]);
    useEffect(() => {
        setNovoLancamento((prev) =>
            createNovoLancamento({
                parametro,
                pessoaId: prev?.pessoaId ?? pessoaIdPadrao,
                dados,
            })
        );

        setMapaAbastecimento((prev) =>
            createMapaAbastecimento({
                dados,
                empresaId,
                pessoaId: prev?.parceiroId ?? pessoaIdPadrao,
            })
        );
    }, [
        dados?.id,
        dados?.data,
        dados?.empresaId,
        empresaId,
        parametro?.centroCustoPDV,
        parametro?.transferenciaPrazo,
        pessoaIdPadrao,
    ]);
    useEffect(() => {
        if (!dados?.empresaId) return;
        (async () => {
            const newParametro = await ParametroAPI.parametroByEmpresaId(dados.empresaId);

            setNovoLancamento((prev) => ({
                ...prev,
                pessoaId: newParametro.pessoaId,
                centroCustoId: newParametro.centroCustoPDV,
                prazoId: newParametro.transferenciaPrazo,
            }));

            // parceiro padrão
            setParceiro(newParametro.pessoaPadrao);

            // carregar a forma

            //carregar o prazo
            const prazo = await getPrazoById(newParametro.transferenciaPrazo);
            setNovoLancamento((prev) => ({
                ...prev,
                pagamentoPrazo: prazo,
            }));

            // carregar a conta

            // carregar objeto centro de custo para o datalist
            if (newParametro.centroCustoPDV) {
                const cc = await FinanceiroAPI.buscaCentroCustoById(newParametro.centroCustoPDV);

                setCentroCusto(cc);
            }
        })();
    }, [dados?.empresaId]);
    //mapa de abastecimento
    useEffect(() => {
        const fetchValidaCNH = async () => {
            try {
                //verificar se o motorista esta apto a conduzir o veiculo
                const validaCNH = await checarCnhHandler({
                    produtoId: mapaAbastecimento.produtoId,
                    pessoaId: mapaAbastecimento.pessoaId,
                    data: mapaAbastecimento.data,
                });

                if (validaCNH.ok === false) {
                    // Transforma em mensagem amigável
                    const { text, severity, title, lines } = formatCnhFeedback(validaCNH);

                    // Se você usa um único flash:
                    setFlashMessage(text, severity, 5);
                }
            } catch (error) {
                console.error(error);
            }
        };

        if (mapaAbastecimento.produtoId) {
            obterVtr(mapaAbastecimento.produtoId);
        }

        if (mapaAbastecimento.produtoId && mapaAbastecimento.pessoaId && divMapaAbastecimento) {
            fetchValidaCNH();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapaAbastecimento.produtoId, mapaAbastecimento.pessoaId, divMapaAbastecimento]);
    // -------------------------
    // helpers / funções
    // -------------------------
    function getFieldName(name) {
        if (!name) return null;
        const parts = name.split('.');
        return parts.length > 1 ? parts[1] : name;
    }
    const pickSelectedId = useCallback((value, field = 'descricao') => {
        // seu padrão: <datalist> option[value="x"] data-id="123"
        const opt = document.querySelector(`datalist option[value="${value}"]`);
        const id = opt?.dataset?.id;
        return id ? Number(id) : null;
    }, []);

    const cacheRef = useRef({
        conta: new Map(),
        forma: new Map(),
        prazo: new Map(),
        centroCusto: new Map(),
    });

    const getContaById = useCallback(async (id) => {
        if (!id) return null;
        const key = Number(id);
        if (cacheRef.current.conta.has(key)) return cacheRef.current.conta.get(key);
        const r = await FinanceiroAPI.buscaContaById(key);
        const data = r?.data || r; // depende do teu API
        cacheRef.current.conta.set(key, data);
        return data;
    }, []);

    const getFormaById = useCallback(async (id) => {
        if (!id) return null;
        const key = Number(id);
        if (cacheRef.current.forma.has(key)) return cacheRef.current.forma.get(key);
        const r = await FinanceiroAPI.buscaFormaById(key);
        const data = r?.data || r;
        cacheRef.current.forma.set(key, data);
        return data;
    }, []);

    const getPrazoById = useCallback(async (id) => {
        if (!id) return null;
        const key = Number(id);
        if (cacheRef.current.prazo.has(key)) return cacheRef.current.prazo.get(key);
        const r = await FinanceiroAPI.buscaPrazoById(key);
        const data = r?.data || r;
        cacheRef.current.prazo.set(key, data);
        return data;
    }, []);

    const getCentroCustoById = useCallback(async (id) => {
        if (!id) return null;
        const key = Number(id);
        if (cacheRef.current.centroCusto.has(key)) return cacheRef.current.centroCusto.get(key);
        const r = await FinanceiroAPI.buscaCentroCustoById(key);
        const data = r?.data || r;
        cacheRef.current.centroCusto.set(key, data);
        return data;
    }, []);

    function createNovoLancamento({ parametro, pessoaId, dados }) {
        return {
            uuid: null,
            id: null,

            caixaId: dados?.id || null,
            pessoaId: pessoaId,
            centroCustoId: parametro?.centroCustoPDV,

            contaId: parametro?.contaId ?? null,
            formaId: null,
            prazoId: parametro?.transferenciaPrazo,
            situacaoId: 1,

            //objetos para o mrt
            Conta: null,
            pagamentoForma: null,
            pagamentoPrazo: null,
            centroCusto: null,

            tipo: 'Receber',
            valorOriginal: null,
            historico: null,

            dataLancamento: dados?.data || null,
            dataVencimento: dados?.data || null,

            ativo: true,
            funcao: 'Adicionar',
            caixa: true,
            mapa: false,
        };
    }
    function createMapaAbastecimento({ dados, empresaId, pessoaId }) {
        return {
            mapa: false,
            ativo: true,
            empresaId: dados?.empresaId || empresaId,
            data: dados?.data || null,
            parceiroId: pessoaId,
        };
    }

    const consultaParceiro = useCallback(
        async (filtro) => {
            if (!dados?.empresaId) return [];
            const r = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: 'Pessoa',
                id: 'id',
                descricao: 'descricao',
                condicao: novoLancamento?.tipo,
                filtro,
                ativo: true,
            });
            setOptionsParceiro(r || []);
            return r;
        },
        [dados?.empresaId, novoLancamento?.tipo]
    );
    const consultaCentroCusto = useCallback(
        async (filtro) => {
            if (!dados?.empresaId) return [];
            const r = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: 'centroCusto',
                id: 'id',
                descricao: 'descricao',
                tipo: novoLancamento?.tipo,
                filtro,
                ativo: true,
            });
            setOptionsCentroCusto(r || []);
            return r;
        },
        [dados?.empresaId, novoLancamento?.tipo]
    );

    const resolvePessoaPadrao = useCallback(async (pessoaId) => {
        if (!pessoaId) return null;
        try {
            const r = await PessoaAPI.pessoaById(pessoaId);

            return r?.data || null;
        } catch {
            return null;
        }
    }, []);

    const consultaFormaById = useCallback(async (id) => {
        if (!id) return null;
        const result = await FinanceiroAPI.buscaFormaById(id);

        setNovoLancamento((prev) => ({
            ...prev,
            pagamentoForma: result,
            contaId: result?.contaId || prev.contaId,
        }));

        return result;
    }, []);

    //mapa de abastecimento
    const buildMapaPayload = useCallback(() => {
        const enabled = !!(divMapaAbastecimento && mapaAbastecimento?.mapa);

        if (!enabled) return null;

        return {
            ...mapaAbastecimento,

            // garante coerência
            ativo: mapaAbastecimento?.ativo ?? true,
            empresaId: mapaAbastecimento?.empresaId ?? dados?.empresaId ?? empresaId,
            data: mapaAbastecimento?.data ?? dados?.data ?? null,

            // parceiro do lançamento (posto/fornecedor etc)
            parceiroId: mapaAbastecimento?.parceiroId ?? novoLancamento?.pessoaId ?? pessoaIdPadrao,

            // mantém valor sincronizado
            valor: mapaAbastecimento?.valor ?? novoLancamento?.valorOriginal ?? null,
        };
    }, [
        divMapaAbastecimento,
        mapaAbastecimento,
        dados?.empresaId,
        dados?.data,
        empresaId,
        novoLancamento?.pessoaId,
        novoLancamento?.valorOriginal,
        pessoaIdPadrao,
    ]);
    const syncCentroCustoMapa = useCallback(
        async (centroCustoId) => {
            if (!centroCustoId) return;
            const result = await FinanceiroAPI.buscaCentroCustoById(centroCustoId);
            if (result && result.combustivel) {
                setDivMapaAbastecimento(true);
                setMapaAbastecimento((prev) => ({ ...prev, mapa: true }));

                //atualiza vtr
                await consultaVTR(dados.empresaId, true, null, 'Móvel');
            } else {
                setDivMapaAbastecimento(false);
                setMapaAbastecimento({ mapa: false, ativo: true });
            }
            setCentroCusto(result);
            setNovoLancamento((prev) => ({ ...prev, centroCustoId: result.id, centroCusto: result }));
            return result;
        },
        [novoLancamento]
    );

    const consultaColaborador = async (empresaId, filtro, condicao) => {
        const result = await PessoaAPI.colaborador(empresaId, filtro, condicao);

        setOptionsMotorista(result);
        // só autopreenche quando o mapa está ativo
        if (mapaAbastecimento?.mapa && Array.isArray(result) && result.length > 0) {
            setMapaAbastecimento((prev) => ({
                ...prev,
                pessoaId: result[0]?.id,
            }));
        }

        return result;
    };

    const consultaVTR = async (empresaId, ativo, filtro, condicao) => {
        try {
            let kmAnterior = 0;
            let produtoId = null;
            let motoristaId = null;

            // Consultas do backend
            const motorista = await consultaColaborador(empresaId, null, 'motorista');
            const result = await ProdutoAPI.bem(empresaId, ativo, filtro, condicao);

            if (Array.isArray(motorista) && motorista.length > 0 && motorista[0]?.id) {
                motoristaId = motorista[0].id;
            }

            if (Array.isArray(result) && result.length > 0 && result[0]?.id) {
                kmAnterior = await ProdutoAPI.ultimoKm(result[0].id);
                produtoId = result[0].id;
            }

            // Atualizar o estado de VTR e usar um callback opcional para continuar
            setOptionsVTR(result);

            setMapaAbastecimento((prev) => ({
                ...prev,
                produtoId: produtoId,
                pessoaId: motoristaId,
                kmAnterior: kmAnterior,
            }));
        } catch (error) {
            console.error('Erro na consulta de VTR:', error);
        }
    };
    const obterVtr = async (produtoId) => {
        try {
            // Faça algo com 'vtr' se necessário
            const km = await ProdutoAPI.ultimoKm(produtoId);

            setMapaAbastecimento((prev) => ({
                ...prev,
                mapa: true,
                kmAnterior: km,
            }));
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        }
    };

    // -------------------------
    // refresh MRT (manual)
    // -------------------------
    const refresh = useCallback(async () => {
        if (!isActive) return;
        if (!dados?.id) return;

        const paging = mrt?.pagination || { pageIndex: 0, pageSize: 5 };

        const resp = await FinanceiroAPI.listarCaixaLancamento({
            start: paging.pageIndex * paging.pageSize,
            size: paging.pageSize,
            filters: JSON.stringify(mrt?.columnFilters ?? []),
            filtersMode: JSON.stringify(mrt?.columnFilterFns ?? []),
            globalFilter: mrt?.globalFilter ?? '',
            sorting: JSON.stringify(mrt?.sorting ?? []),
            caixaId: dados.id,
        });

        const rows = resp?.data?.data || [];
        const total = resp?.data?.meta?.totalRowCount ?? rows.length;

        setCaixaLancamentos(rows);
        mrt?.setRowCount?.(total);
    }, [isActive, dados?.id, mrt]);

    // quando o MRT state mudar, refaz refresh
    useEffect(() => {
        if (!isActive) return;
        if (!mrt) return;
        if (!dados?.id) return;
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isActive,
        dados?.id,
        mrt?.pagination?.pageIndex,
        mrt?.pagination?.pageSize,
        JSON.stringify(mrt?.columnFilters ?? []),
        JSON.stringify(mrt?.columnFilterFns ?? []),
        mrt?.globalFilter,
        JSON.stringify(mrt?.sorting ?? []),
    ]);

    // -------------------------
    // change / blur / focus
    // -------------------------

    const change = useCallback(
        async (e) => {
            if (!e?.target) return;

            const { name, value, type, checked } = e.target;
            const next = type === 'checkbox' ? checked : value;
            const field = getFieldName(name);

            if (name?.startsWith('novoLancamento.')) {
                if (field === 'formaId') {
                    const formaId = Number(next) || null;
                    setNovoLancamento((p) => ({ ...p, formaId: formaId }));

                    const forma = await consultaFormaById(formaId);
                    if (forma) {
                        setNovoLancamento((p) => ({
                            ...p,
                            pagamentoForma: forma,
                            //se a forma tem contaId padrão, já puxa e seta Conta também
                            contaId: forma?.contaId ?? p.contaId,
                        }));
                        if (forma?.contaId) {
                            const conta = await getContaById(forma.contaId);
                            setNovoLancamento((p) => ({ ...p, Conta: conta ?? p.Conta }));
                        }
                    }
                    return;
                }
                if (field === 'contaId') {
                    const contaId = Number(next) || null;
                    setNovoLancamento((p) => ({ ...p, contaId }));

                    const conta = await getContaById(contaId);
                    if (conta) setNovoLancamento((p) => ({ ...p, Conta: conta }));
                    return;
                }
                if (field === 'prazoId') {
                    const prazoId = Number(next) || null;
                    setNovoLancamento((p) => ({ ...p, prazoId }));

                    const prazo = await getPrazoById(prazoId);
                    if (prazo) setNovoLancamento((p) => ({ ...p, pagamentoPrazo: prazo }));

                    return;
                }
                if (field === 'valorOriginal') {
                    const v = formatarNumeroDecimal(next);
                    setNovoLancamento((p) => ({ ...p, valorOriginal: v }));
                    setMapaAbastecimento((p) => ({ ...p, valor: v }));
                    return;
                }
                if (field === 'tipo') {
                    // 1) atualiza tipo
                    setNovoLancamento((p) => ({ ...p, tipo: next }));

                    // 2) se já tenho parceiro carregado, reaplica defaults
                    // (se não tiver, busca pela pessoaId atual)
                    const pessoaAtual = await resolvePessoaPadrao(novoLancamento?.pessoaId || pessoaIdPadrao);

                    if (pessoaAtual) {
                        setParceiro(pessoaAtual);

                        setNovoLancamento((p) =>
                            applyLancamentoPessoa({
                                prev: { ...p, tipo: next },
                                pessoa: pessoaAtual,
                                tipo: next,
                            })
                        );
                    }

                    // 3) como o tipo influencia datalist de centro de custo, opcionalmente:
                    // setOptionsCentroCusto([]); // (se quiser limpar a lista)
                    // setCentroCusto(null);      // (se quiser forçar escolher outro)

                    return;
                }

                setNovoLancamento((p) => ({ ...p, [field]: next }));
                return;
            }

            if (name?.startsWith('Parceiro.')) {
                setParceiro((p) => ({ ...(p || {}), [field]: next }));

                // carrega datalist
                await consultaParceiro(next);

                // resolve id selecionado
                const selectedId = pickSelectedId(next);

                if (selectedId) {
                    const { data } = await PessoaAPI.pessoaById(selectedId);

                    setParceiro(data);
                    setNovoLancamento((p) => ({ ...p, pessoaId: selectedId }));
                    setMapaAbastecimento((p) => ({ ...p, parceiroId: selectedId }));

                    const isPagar = novoLancamento.tipo === 'Pagar';
                    const ccId = isPagar ? data?.centroCustoFornecedorId : data?.centroCustoClienteId;

                    if (ccId) {
                        await syncCentroCustoMapa(ccId);
                    }
                    // fallback inteligente do prazo
                    let pzId = isPagar ? data?.prazoFornecedorId : data?.prazoClienteId;

                    if (!pzId) {
                        pzId = parametro?.transferenciaPrazo;
                    }

                    if (pzId) {
                        const prazo = await getPrazoById(pzId);

                        setNovoLancamento((p) => ({
                            ...p,
                            prazoId: pzId,
                            pagamentoPrazo: prazo,
                            centroCustoId: ccId,
                            Pessoa: data,
                        }));
                    }
                }
                return;
            }

            if (name?.startsWith('centroCusto.')) {
                setCentroCusto((p) => ({ ...(p || {}), [field]: next }));

                await consultaCentroCusto(next);

                const selectedId = pickSelectedId(next);
                if (selectedId) {
                    const cc = await getCentroCustoById(selectedId);

                    // ativa mapa se combustivel
                    if (cc?.combustivel) {
                        setDivMapaAbastecimento(true);
                        setMapaAbastecimento((p) => ({ ...p, mapa: true }));
                    } else {
                        setDivMapaAbastecimento(false);
                        setMapaAbastecimento((p) => ({ ...p, mapa: false }));
                    }
                    setCentroCusto(cc);
                    setNovoLancamento((p) => ({ ...p, centroCustoId: selectedId, centroCusto: cc }));
                }
                return;
            }

            if (name?.startsWith('mapaAbastecimento.')) {
                let v = next;

                if (field === 'litros' || field === 'preco') {
                    v = formatarNumeroDecimal(next);
                }

                setMapaAbastecimento((prev) => {
                    const updated = { ...prev, [field]: v };

                    if (field === 'litros' || field === 'preco') {
                        const litros = Number(updated?.litros || 0);
                        const preco = Number(updated?.preco || 0);
                        const total = Number((litros * preco).toFixed(2));

                        if (total > 0) {
                            setNovoLancamento((p) => ({ ...p, valorOriginal: total.toFixed(2) }));
                            updated.valor = total;
                        } else {
                            setNovoLancamento((p) => ({ ...p, valorOriginal: null }));
                            updated.valor = null;
                        }
                    }

                    return updated;
                });

                return;
            }
        },
        [setNovoLancamento, consultaParceiro, consultaCentroCusto, pickSelectedId, setMapaAbastecimento]
    );

    const blur = useCallback(
        (e) => {
            if (!e?.target) return;

            const { name, value } = e.target;

            if (name === 'mapaAbastecimento.km') {
                const kmAnterior = Number(mapaAbastecimento?.kmAnterior || 0);
                const kmAtual = Number(value || 0);

                if (kmAtual && kmAnterior && kmAtual < kmAnterior) {
                    setFlashMessage?.('Km atual é menor que o km anterior?', 'error');
                }
            }
        },
        [mapaAbastecimento?.kmAnterior, setFlashMessage]
    );

    const focus = useCallback(() => {}, []);

    // -----------------------------------------------------
    // ações: limpar / salvar / editar / deletar / imprmir
    // -----------------------------------------------------
    const limparLancamento = useCallback(() => {
        setNovoLancamento((prev) => ({
            ...prev,
            uuid: null,
            id: null,

            caixaId: dados.id,
            pessoaId: pessoaIdPadrao,
            centroCustoId: parametro.centroCustoPDV,
            prazoId: parametro.transferenciaPrazo,
            situacaoId: 1,

            funcao: 'Adicionar',
            valorOriginal: null,
            historico: null,
            tipo: 'Receber',

            dataLancamento: dados?.data,
            dataVencimento: dados?.data,
        }));

        setParceiro(parametro.pessoaPadrao);
        setMapaAbastecimento({ mapa: false, ativo: true });
        setDivMapaAbastecimento(false);
    }, [dados?.data, parametro?.centroCustoPDV, parametro?.transferenciaPrazo, pessoaIdPadrao]);

    const handleLancamento = useCallback(
        (originalRow, rowIndex, funcao) => {
            // preenche para editar/deletar
            setParceiro(originalRow?.Pessoa || null);
            setCentroCusto(originalRow?.centroCusto || null);
            setForma(originalRow.pagamento || null);
            setPrazo(originalRow.pagamentoPrazo || null);

            setNovoLancamento({
                id: originalRow.id,
                uuid: originalRow.uuid,
                ativo: originalRow.ativo,
                funcao,
                index: rowIndex,

                pessoaId: originalRow.pessoaId,
                caixaId: originalRow.caixaId,
                centroCustoId: originalRow.centroCustoId,
                contaId: originalRow.contaId,
                formaId: originalRow.formaId,
                prazoId: originalRow.prazoId,
                situacaoId: originalRow.situacaoId,

                tipo: originalRow.tipo,
                valorOriginal: originalRow.valorOriginal,
                historico: originalRow.historico,
                dataLancamento: dados?.data || originalRow.dataLancamento,
                dataVencimento: dados?.data || originalRow.dataVencimento,
                caixaMapaAbastecimento: dados?.caixaMapaAbastecimento || originalRow?.caixaMapaAbastecimento,

                caixa: true,
            });

            // mapa
            if (originalRow?.caixaMapaAbastecimento?.mapa) {
                setMapaAbastecimento(originalRow.caixaMapaAbastecimento);
                setDivMapaAbastecimento(true);
            } else {
                setDivMapaAbastecimento(false);
            }
        },
        [dados?.data]
    );
    const handleLancamentos = useCallback(async () => {
        const isAdicionar = novoLancamento.funcao === 'Adicionar';
        const isEditar = novoLancamento.funcao === 'Editar';
        const isDeletar = novoLancamento.funcao === 'Deletar';

        if (isAdicionar || isEditar) {
            if (!novoLancamento?.centroCustoId) {
                setFlashMessage?.('Centro de Custo não pode ser vazio!', 'error');
                return;
            }
            if (Number(novoLancamento.valorOriginal) <= 0) {
                setFlashMessage?.('Valor Original deve ser maior que zero!', 'error');
                return;
            }

            // ✅ se mapa está ligado, valida campos mínimos
            if (divMapaAbastecimento && mapaAbastecimento?.mapa) {
                if (!mapaAbastecimento?.produtoId) {
                    setFlashMessage?.('Selecione o veículo (VTR).', 'error');
                    return;
                }
                if (!mapaAbastecimento?.pessoaId) {
                    setFlashMessage?.('Selecione o motorista.', 'error');
                    return;
                }
                if (!mapaAbastecimento?.km) {
                    setFlashMessage?.('Informe o KM atual.', 'error');
                    return;
                }
            }
        }

        const mapPayload = buildMapaPayload();

        // ----------------------------------
        // OFFLINE (caixa ainda não salvo)
        // ----------------------------------
        if (!dados?.id) {
            setCaixaLancamentos((prev) => [
                ...prev,
                {
                    ...novoLancamento,
                    Pessoa: Parceiro,
                    centroCusto,
                    caixaMapaAbastecimento: mapPayload, // ✅ aqui estava faltando
                },
            ]);
            limparLancamento();
            return;
        }

        // ----------------------------------
        // ONLINE (salva no backend)
        // ----------------------------------
        const payload = {
            ...novoLancamento,
            caixaId: dados.id,
            caixaMapaAbastecimento: mapPayload, // ✅ sempre pronto e completo
        };

        let r = null;

        if (isDeletar) {
            r = await FinanceiroAPI.deletarLancamentoCaixa(novoLancamento);
        } else {
            r = await FinanceiroAPI.managerCaixaLancamento(payload);
        }

        setFlashMessage?.(r.message, r.type);

        await refresh();
        limparLancamento();
    }, [
        novoLancamento,
        dados?.id,
        Parceiro,
        centroCusto,
        divMapaAbastecimento,
        mapaAbastecimento,
        buildMapaPayload,
        refresh,
        limparLancamento,
        setFlashMessage,
    ]);
    const onEnterTab = useCallback(async () => {
        // 1) aplica boot base (parametros + datas + caixaId)
        const base = applyLancamentoBoot({
            prev: novoLancamento,
            parametro,
            dados,
            pessoaId: novoLancamento?.pessoaId ?? pessoaIdPadrao,
        });

        // 2) busca pessoa (defaults vêm dela)
        const pessoa = await resolvePessoaPadrao(base.pessoaId);

        // 3) aplica defaults da pessoa
        const finalLanc = pessoa ? applyLancamentoPessoa({ prev: base, pessoa, tipo: base.tipo }) : base;

        // 3.1) popula as tabelas filhas
        const conta = finalLanc?.contaId ? await getContaById(finalLanc.contaId) : null;
        const forma = finalLanc?.formaId ? await getFormaById(finalLanc.formaId) : null;
        const prazo = finalLanc?.prazoId ? await getPrazoById(finalLanc.prazoId) : null;
        const cc = finalLanc?.centroCustoId ? await getCentroCustoById(finalLanc.centroCustoId) : null;

        setNovoLancamento((p) => ({
            ...finalLanc,
            Conta: conta,
            pagamentoForma: forma,
            pagamentoPrazo: prazo,
            centroCusto: cc,
            Pessoa: pessoa,
        }));
        setCentroCusto(cc);
        if (cc?.combustivel) {
            setDivMapaAbastecimento(true);
            setMapaAbastecimento((m) => ({ ...m, mapa: true }));
            await consultaVTR(dados.empresaId, true, null, 'Móvel');
        }

        // 4) set estado
        setParceiro(pessoa);

        // 5) refresh da grid (se já tem caixa salvo)
        if (dados?.id) {
            await refresh();
            return;
        }
    }, [dados, parametro, novoLancamento, pessoaIdPadrao, resolvePessoaPadrao, refresh]);

    const imprimirRecibo = useCallback(async (obj) => {
        const caminho = 'reciboCaixaAberto';
        const url = 'reciboCaixaAberto';

        const response = await relatorios(caminho, url, obj);

        // Cria um Blob a partir dos dados do arraybuffer
        const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
        });

        // Cria um URL temporário para o Blob
        const pdfUrl = URL.createObjectURL(pdfBlob);

        // Use o URL do PDF para abrir em uma nova janela ou realizar outras ações
        window.open(pdfUrl);
    }, []);

    // -------------------------
    // state + actions
    // -------------------------
    const state = useMemo(
        () => ({
            btnText,

            navigate,
            relatorios,

            caixaLancamentos,
            optionsTipo,
            optionsConta,

            optionsForma,
            optionsPrazo,

            optionsParceiro,
            Parceiro,

            optionsCentroCusto,
            centroCusto,

            optionsMotorista,
            optionsVTR,

            divMapaAbastecimento,
            mapaAbastecimento,

            novoLancamento,

            rowCount: mrt?.rowCount ?? 0,
        }),
        [
            mrt,
            btnText,
            caixaLancamentos,
            optionsTipo,
            optionsConta,
            optionsForma,
            optionsPrazo,
            optionsParceiro,
            Parceiro,
            optionsCentroCusto,
            centroCusto,
            optionsMotorista,
            optionsVTR,
            divMapaAbastecimento,
            mapaAbastecimento,
            novoLancamento,
        ]
    );

    const actions = useMemo(
        () => ({
            change,
            blur,
            focus,
            refresh,
            limparLancamento,
            handleLancamento,
            handleLancamentos,
            onEnterTab,
            imprimirRecibo,
        }),
        [change, blur, focus, refresh, limparLancamento, handleLancamento, handleLancamentos, imprimirRecibo]
    );

    return { state, actions };
}
